import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import AddBriefPanel from '../../components/add-brief-panel/AddBriefPanel';
import DesktopSidebar from '../../components/desktop-sidebar/desktopSidebar';
import EmailEnforcer from '../../components/email-enforcer/EmailEnforcer';
import ErrorReporter from '../../components/error-reporter/ErrorReporter';
import { IOSInstallModal } from '../../components/ios-install-modal/iosInstallModal';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import { MobileSidebar } from '../../components/mobile-sidebar/mobileSidebar';
import NotificationHub from '../../components/notification-hub/NotificationHub';
import PaywallModal from '../../components/paywall-modal/paywallModal';
import TimezoneInferer from '../../components/timezone-inferer/timezoneInferer';
import ToastNotifications from '../../components/toast-notifications/ToastNotifications';
import { usePaywallContext } from '../../contexts/paywall';
import { useAccount } from '../../hooks/useAccount';
import { useBriefsOverview } from '../../hooks/useBriefs';
import { useApplicationAlerts } from '../../hooks/useApplicationAlert';
import { closeMenu, dismissNotifHub, useDashboardConfiguration } from '../../state/app-state';
import DefaultPageLayout from '../defaultPageLayout';

interface DashboardLayoutProps {
  title?: string;
  children?: React.ReactNode;
}

const DashboardPageLayout: FunctionComponent<DashboardLayoutProps> = ({ title, children }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const match = useRouteMatch();
  const { search } = useLocation();

  const alerts = useApplicationAlerts();
  const { paywallModalShown, setPaywallModalShown } = usePaywallContext();

  const dashboardConfiguration = useDashboardConfiguration();
  const isNotificationHubShown = dashboardConfiguration.isNotifHubOpen === true;
  const isMenuOpen = dashboardConfiguration.isMenuOpen === true;
  const [openAddNewBrief, setOpenAddNewBrief] = useState(false);
  const { account, loading: isLoadingAccount, refetch: refetchAccount } = useAccount();
  const { briefs: briefItems, loading: isFetchingBriefs } = useBriefsOverview();
  const maxBriefCount = account?.product?.features.briefs ?? 0;
  const [iOsInstallModalShown, setIOsInstallModalShown] = useState(false);

  const [currentBriefCount, setCurrentBriefCount] = useState(0);

  useEffect(() => {
    const briefQueryParam = new URLSearchParams(search).get('brief');
    setOpenAddNewBrief(!!briefQueryParam && briefQueryParam === 'new');
  }, [search]);

  useEffect(() => {
    // Close our navigation panel on path changes
    closeMenu();
  }, [pathname]);

  useEffect(() => {
    if (!briefItems) {
      return;
    }

    setCurrentBriefCount(briefItems.length);
  }, [briefItems]);

  if (isLoadingAccount || !account || isFetchingBriefs) {
    return <LoadingSpinner />;
  }

  const handleAddNewBriefClick = () => {
    history.push(`${match.url}?brief=new`);
  };

  const handleTwitterSignInSuccess = async () => {
    // Refetch up-to-date user data (the social page gates on the Twitter
    // metadata it returns), then navigate.
    await refetchAccount();
    history.push('/dashboard/social');
  };

  return (
    <DefaultPageLayout title={title}>
      <div className="h-full flex bg-white">
        <MobileSidebar
          account={account}
          briefs={briefItems || []}
          currentBriefCount={currentBriefCount}
          maxBriefCount={maxBriefCount}
          sidebarOpen={isMenuOpen}
          onCloseSidebar={() => closeMenu()}
          onAddNewBrief={handleAddNewBriefClick}
          onTwitterSignInSuccess={handleTwitterSignInSuccess}
          onIosAppInstall={() => setIOsInstallModalShown(true)}
        />

        {/* Static sidebar for desktop */}
        <div className="hidden md:flex md:shrink-0">
          <DesktopSidebar
            account={account}
            briefs={briefItems || []}
            currentBriefCount={currentBriefCount}
            maxBriefCount={maxBriefCount}
            onAddNewBrief={handleAddNewBriefClick}
            onTwitterSignInSuccess={handleTwitterSignInSuccess}
          />
        </div>

        {/* Main Area */}
        <div className="min-w-0 flex flex-1 overflow-hidden">
          {/* Children Component Area */}
          {children}
        </div>

        {openAddNewBrief && (
          <AddBriefPanel
            account={account}
            panelOpen={openAddNewBrief}
            onClosePanel={() => history.push(match.url)}
          />
        )}

        {iOsInstallModalShown && (
          <IOSInstallModal
            isOpen={iOsInstallModalShown}
            onClose={() => setIOsInstallModalShown(false)}
          />
        )}

        {/* Mobile Upsell notification */}
        {isNotificationHubShown && (
          <NotificationHub
            account={account}
            alerts={alerts}
            isVisible={isNotificationHubShown}
            onDismiss={() => dismissNotifHub()}
          />
        )}

        {paywallModalShown && (
          <PaywallModal isOpen={paywallModalShown} onClose={() => setPaywallModalShown(false)} />
        )}

        <ToastNotifications />
        <ErrorReporter />
        <TimezoneInferer />
        <EmailEnforcer />
      </div>
    </DefaultPageLayout>
  );
};

export default DashboardPageLayout;
